import type { ReactNode } from 'react'
import { Modal } from '@/components/ui/Modal/Modal'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Style the confirm action as destructive (red). */
  danger?: boolean
  /** Disable the buttons + show a busy label while the action runs. */
  loading?: boolean
}

/**
 * Accessible confirm/cancel dialog built on the shared Modal shell. Replaces
 * window.confirm for actions that need a clear explanation (e.g. scheduling a
 * deletion). The confirm button can be styled as destructive.
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
}: ConfirmDialogProps) {
  const confirmClass = danger
    ? 'bg-red-500 text-white hover:bg-red-500/80 focus:ring-red-400'
    : 'bg-neon-cyan text-navy-dark hover:bg-neon-cyan/80 focus:ring-neon-cyan'

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-gray-600/20 text-gray-300 hover:bg-gray-600/30 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm leading-relaxed text-gray-300">{description}</div>
    </Modal>
  )
}
